import React from "react";
import { FaAddressBook, FaBuilding, FaHome, FaImage, FaPalette } from "react-icons/fa";

import SettingsBlock from "./SettingsBlock";

import {
  ColorField,
  ImageUrlField,
  InputField,
  QuillField,
  SelectField,
} from "./SettingsFields";

import { resolveMediaUrl } from "../utils/settingsManagerUtils";

export default function SettingsForm({
  settings,
  galleries,
  loading,
  saving,
  demoMode,
  onChange,
  onFieldChange,
  onSubmit,
}) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="h-56 animate-pulse rounded-[28px] bg-slate-200 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <SettingsBlock
        icon={<FaBuilding />}
        title="Identité du portail"
        subtitle="Ces informations permettent d’identifier la commune ou la structure porteuse du portail associatif."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <InputField
            label="Nom complet"
            name="nom_asso"
            value={settings.nom_asso}
            onChange={onChange}
            placeholder="Ex : Ville du Bouscat"
          />

          <InputField
            label="Nom court"
            name="shortname_asso"
            value={settings.shortname_asso}
            onChange={onChange}
            placeholder="Ex : Le Bouscat"
          />

          <InputField
            label="Responsable"
            name="responsable_asso"
            value={settings.responsable_asso}
            onChange={onChange}
            placeholder="Nom du responsable"
          />

          <InputField
            label="SIRET"
            name="siret_asso"
            value={settings.siret_asso}
            onChange={onChange}
            placeholder="Numéro SIRET"
          />

          <div className="md:col-span-2">
            <ImageUrlField
              label="Logo"
              name="logo_asso"
              value={settings.logo_asso}
              onChange={onChange}
              preview={resolveMediaUrl(settings.logo_asso)}
            />
          </div>
        </div>
      </SettingsBlock>

      <SettingsBlock
        icon={<FaAddressBook />}
        title="Coordonnées"
        subtitle="Ces informations sont utilisées dans le footer, la page contact et les informations publiques."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <InputField
            label="Adresse"
            name="adresse_asso"
            value={settings.adresse_asso}
            onChange={onChange}
            placeholder="Adresse complète"
          />

          <InputField
            label="Commune"
            name="commune_asso"
            value={settings.commune_asso}
            onChange={onChange}
            placeholder="Commune"
          />

          <InputField
            label="Téléphone"
            name="tel_asso"
            value={settings.tel_asso}
            onChange={onChange}
            placeholder="05..."
          />

          <InputField
            label="Email"
            name="mail_asso"
            type="email"
            value={settings.mail_asso}
            onChange={onChange}
            placeholder="contact@..."
          />

          <InputField
            label="Site web"
            name="siteweb_asso"
            value={settings.siteweb_asso}
            onChange={onChange}
            placeholder="https://..."
          />

          <InputField
            label="Page Facebook"
            name="facebook_asso"
            value={settings.facebook_asso}
            onChange={onChange}
            placeholder="https://facebook.com/..."
          />

          <InputField
            label="Page Instagram"
            name="instagram_asso"
            value={settings.instagram_asso}
            onChange={onChange}
            placeholder="https://instagram.com/..."
          />

          <ColorField
            label="Couleur primaire"
            name="primary_color"
            value={settings.primary_color}
            onChange={onChange}
          />
        </div>
      </SettingsBlock>

      <SettingsBlock
        icon={<FaHome />}
        title="Page d’accueil"
        subtitle="Configurez le hero principal, les images d’en-tête et la galerie utilisée pour le diaporama."
      >
        <div className="grid gap-4">
          <ImageUrlField
            label="Image header 1"
            name="header1_photo_url"
            value={settings.header1_photo_url}
            onChange={onChange}
            preview={resolveMediaUrl(settings.header1_photo_url)}
          />

          <ImageUrlField
            label="Image header 2"
            name="header2_photo_url"
            value={settings.header2_photo_url}
            onChange={onChange}
            preview={resolveMediaUrl(settings.header2_photo_url)}
          />

          <QuillField
            label="Titre du header"
            value={settings.header_title}
            onChange={(value) => onFieldChange("header_title", value)}
            minHeight={120}
          />

          <InputField
            label="Sous-titre du header"
            name="header_subtitle"
            value={settings.header_subtitle}
            onChange={onChange}
            placeholder="Texte court sous le titre principal"
          />

          <SelectField
            label="Galerie du carrousel"
            name="carrousel_id"
            value={settings.carrousel_id}
            onChange={onChange}
          >
            <option value="">Aucune galerie sélectionnée</option>
            {galleries.map((gallery) => (
              <option key={gallery.id} value={gallery.id}>
                {gallery.titre}
              </option>
            ))}
          </SelectField>
        </div>
      </SettingsBlock>

      <SettingsBlock
        icon={<FaImage />}
        title="Section À propos"
        subtitle="Texte de présentation affiché sur la page d’accueil."
      >
        <div className="grid gap-4">
          <InputField
            label="Titre de la section"
            name="about_title"
            value={settings.about_title}
            onChange={onChange}
            placeholder="Ex : Le portail des associations"
          />

          <QuillField
            label="Contenu de présentation"
            value={settings.about_content}
            onChange={(value) => onFieldChange("about_content", value)}
            minHeight={220}
          />
        </div>
      </SettingsBlock>

      <div className="sticky bottom-4 z-30 rounded-[24px] border border-slate-200 bg-white/90 p-3 shadow-2xl backdrop-blur dark:border-white/10 dark:bg-slate-900/90">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Pensez à enregistrer vos modifications
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Les changements ne seront visibles côté public qu’après validation.
            </p>
          </div>

          <button
            type="submit"
            disabled={saving || demoMode}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FaPalette />
            {saving ? "Enregistrement..." : "Enregistrer les paramètres"}
          </button>
        </div>
      </div>
    </form>
  );
}